'use client'

import { motion } from 'framer-motion'
import Link from 'next/link'
import { FileText, Code2, GitMerge, ShieldCheck, AlertTriangle, RefreshCw, Scale, ArrowRight } from 'lucide-react'
import { EASE_OUT, EASE_CUSTOM } from '@/lib/animations'

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.9, ease: EASE_CUSTOM },
}

const stagger = (i: number) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.8, delay: i * 0.08, ease: EASE_CUSTOM },
})

const processSteps = [
  {
    step: '01',
    title: 'Diagnóstico gratuito',
    desc: 'El cliente solicita una sesión de diagnóstico sin coste a través del formulario o por WhatsApp. En ella analizamos la situación actual, las necesidades del negocio y el alcance del proyecto.',
    color: 'from-blue-500 to-indigo-600',
  },
  {
    step: '02',
    title: 'Propuesta técnica y económica',
    desc: 'Innovathera Technology elabora una propuesta detallada que incluye alcance funcional, tecnologías, plazos estimados y presupuesto. Dicha propuesta se envía al cliente en formato escrito.',
    color: 'from-indigo-500 to-violet-600',
  },
  {
    step: '03',
    title: 'Aceptación y formalización',
    desc: 'El cliente acepta la propuesta de forma escrita (por correo electrónico o firma del contrato de servicios). En ese momento se considera formalizado el acuerdo y se acuerda la forma de pago.',
    color: 'from-violet-500 to-purple-600',
  },
  {
    step: '04',
    title: 'Desarrollo y entrega',
    desc: 'Se inicia el desarrollo del proyecto conforme a las especificaciones acordadas. Se realizan entregas parciales para validación del cliente. La entrega final se produce una vez completado el desarrollo y superadas las pruebas de aceptación.',
    color: 'from-emerald-500 to-teal-600',
  },
]

const services = [
  'Desarrollo de software a medida para empresas y autónomos',
  'Sistemas de reservas y gestión de citas online',
  'Pasarelas de pago e integración con sistemas de cobro',
  'CRM personalizados y sistemas de gestión de clientes',
  'Automatizaciones de procesos de negocio (RPA, integraciones API)',
  'Aplicaciones web y plataformas digitales a medida',
  'Consultoría y auditoría de sistemas digitales',
]

const sections = [
  {
    icon: FileText,
    title: '1. Objeto y aceptación',
    color: 'text-blue-600',
    bg: 'from-blue-50 to-indigo-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed">
          Las presentes Condiciones Generales de Contratación (en adelante, "las Condiciones") regulan el acceso y uso del sitio web <a href="https://innovatheratech.com" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">https://innovatheratech.com</a> y la contratación de los servicios profesionales prestados por <span className="font-semibold text-slate-800">Teodor Chavdarov</span> (NIF <span className="font-semibold text-slate-800">X8562855R</span>), trabajador autónomo (empresario individual) que ejerce su actividad bajo el nombre comercial <span className="font-semibold text-slate-800">Innovathera Technology</span>, con domicilio profesional en Calle Costa Verde Nº 5, 3B, 28400 Collado Villalba, Madrid, España (en adelante, "Innovathera Technology" o "el Profesional"). El acceso al sitio web o la solicitud de cualquier servicio implica la aceptación plena y sin reservas de las presentes Condiciones en la versión publicada en el momento del acceso. Si no está de acuerdo con estas Condiciones, le rogamos que no utilice el sitio web ni solicite nuestros servicios.
        </p>
        <p className="text-slate-500 text-sm mt-3">
          Las facturas emitidas por Innovathera Technology cumplen con el Reglamento de Sistemas Informáticos de Facturación (Verifactu) mediante el uso de Holded como software de facturación certificado.
        </p>
      </>
    ),
  },
  {
    icon: Code2,
    title: '2. Servicios ofrecidos',
    color: 'text-indigo-600',
    bg: 'from-indigo-50 to-violet-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Innovathera Technology ofrece los siguientes servicios profesionales de desarrollo tecnológico:
        </p>
        <ul className="space-y-2 text-slate-600 text-sm">
          {services.map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-indigo-400" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-slate-500 text-sm mt-3 italic">
          Las características específicas, alcance y precio de cada servicio se detallarán en la propuesta técnica y económica correspondiente.
        </p>
      </>
    ),
  },
  {
    icon: GitMerge,
    title: '3. Proceso de contratación',
    color: 'text-violet-600',
    bg: 'from-violet-50 to-purple-50',
    content: (
      <div className="space-y-3">
        {processSteps.map((step) => (
          <div key={step.step} className="flex items-start gap-4 bg-white/60 rounded-xl p-4 border border-slate-200/30">
            <div className={`shrink-0 w-9 h-9 rounded-lg bg-gradient-to-br ${step.color} flex items-center justify-center text-white font-bold text-xs`}>
              {step.step}
            </div>
            <div>
              <p className="font-semibold text-slate-800 text-sm mb-1">{step.title}</p>
              <p className="text-slate-500 text-sm leading-relaxed">{step.desc}</p>
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: ShieldCheck,
    title: '4. Propiedad intelectual del código entregado',
    color: 'text-emerald-600',
    bg: 'from-emerald-50 to-teal-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Una vez abonada la totalidad del importe acordado, <span className="font-semibold text-slate-800">el cliente es el único y exclusivo propietario del código fuente y del software entregado</span>. Innovathera Technology cede todos los derechos de propiedad intelectual sobre el desarrollo a medida al cliente, incluyendo el derecho de uso, reproducción, modificación y distribución.
        </p>
        <p className="text-slate-600 leading-relaxed">
          Se excluyen de esta cesión los componentes de terceros (librerías de código abierto, frameworks, herramientas SaaS) sujetos a sus propias licencias, así como las herramientas o plantillas internas de Innovathera Technology que no formen parte del desarrollo específico del proyecto. El cliente acepta el uso de componentes de código abierto bajo licencias permisivas (MIT, Apache 2.0, BSD, etc.) que le permiten su uso comercial sin restricciones.
        </p>
      </>
    ),
  },
  {
    icon: ShieldCheck,
    title: '5. Garantías y soporte post-entrega',
    color: 'text-cyan-600',
    bg: 'from-cyan-50 to-sky-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Innovathera Technology ofrece un período de garantía de <span className="font-semibold text-slate-800">un (1) mes natural</span> desde la fecha de entrega final del proyecto. Durante este período, la Empresa corregirá sin coste adicional cualquier defecto o error de funcionamiento que sea imputable al desarrollo realizado, siempre que:
        </p>
        <ul className="space-y-2 text-slate-600 text-sm">
          {[
            'El defecto se manifieste bajo condiciones normales de uso conforme a las especificaciones acordadas.',
            'El cliente no haya modificado el código fuente entregado sin supervisión de la Empresa.',
            'El defecto sea notificado por escrito dentro del período de garantía.',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-cyan-400" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-slate-600 leading-relaxed mt-3">
          Transcurrido el período de garantía, los servicios de soporte y mantenimiento estarán sujetos a un acuerdo de nivel de servicio (SLA) independiente y a la tarifa vigente en cada momento. Las solicitudes de nuevas funcionalidades o cambios de alcance no están cubiertas por la garantía.
        </p>
      </>
    ),
  },
  {
    icon: AlertTriangle,
    title: '6. Limitación de responsabilidad',
    color: 'text-amber-600',
    bg: 'from-amber-50 to-orange-50',
    content: (
      <>
        <p className="text-slate-600 leading-relaxed mb-3">
          Innovathera Technology no será responsable de los daños o perjuicios que pudieran derivarse de:
        </p>
        <ul className="space-y-2 text-slate-600 text-sm">
          {[
            'El uso incorrecto del software entregado por parte del cliente o de terceros.',
            'Fallos o interrupciones en servicios de terceros (proveedores de hosting, APIs externas, servicios SaaS).',
            'Pérdida de datos producida por causas ajenas a Innovathera Technology',
            'Modificaciones realizadas sobre el código por el cliente o terceros sin supervisión de la Empresa.',
            'Decisiones empresariales adoptadas por el cliente basadas en los resultados del software.',
            'Daños indirectos, lucro cesante o pérdida de negocio.',
          ].map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400" />
              {item}
            </li>
          ))}
        </ul>
        <p className="text-slate-600 leading-relaxed mt-3">
          En todo caso, la responsabilidad máxima de Innovathera Technology quedará limitada al importe total abonado por el cliente por el servicio concreto que haya dado lugar al daño reclamado.
        </p>
      </>
    ),
  },
  {
    icon: RefreshCw,
    title: '7. Modificaciones de las condiciones',
    color: 'text-rose-600',
    bg: 'from-rose-50 to-pink-50',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Innovathera Technology se reserva el derecho a modificar las presentes Condiciones en cualquier momento. Los cambios entrarán en vigor desde el momento de su publicación en el sitio web. El uso continuado del sitio web o la solicitud de nuevos servicios tras la publicación de las modificaciones implica la aceptación de las nuevas Condiciones. Para los proyectos en curso, las Condiciones aplicables serán las vigentes en el momento de la formalización del contrato.
      </p>
    ),
  },
  {
    icon: Scale,
    title: '8. Ley aplicable y jurisdicción',
    color: 'text-slate-600',
    bg: 'from-slate-50 to-gray-50',
    content: (
      <p className="text-slate-600 leading-relaxed">
        Las presentes Condiciones se rigen e interpretan de conformidad con la legislación española vigente, en particular la Ley 34/2002 de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSICE), la Ley Orgánica 3/2018 (LOPDGDD) y el Reglamento General de Protección de Datos (RGPD). Para la resolución de cualquier controversia o litigio derivado del acceso al sitio web o de la prestación de los servicios, las partes se someten a la jurisdicción de los <span className="font-semibold text-slate-800">Juzgados y Tribunales de la ciudad de Madrid</span>, renunciando expresamente a cualquier otro fuero que pudiera corresponderles.
      </p>
    ),
  },
]

export default function TerminosClient() {
  return (
    <section className="pt-28 pb-20 relative z-10">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-5">
            <Scale className="w-3.5 h-3.5 text-slate-600" />
            <span className="text-xs font-mono tracking-[0.25em] text-slate-600 uppercase">Condiciones legales</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-slate-900 mb-4">
            Términos y{' '}
            <span className="text-blue-600">Condiciones</span>
          </h1>
          <p className="text-slate-500 text-lg font-light max-w-2xl mx-auto leading-relaxed">
            Estas condiciones regulan la relación entre Innovathera Technology y sus clientes en la prestación de servicios de desarrollo de software a medida.
          </p>
          <p className="text-xs font-mono text-slate-400 mt-4 tracking-wider uppercase">
            Última actualización: Mayo 2026
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-5">
          {sections.map((s, i) => {
            const Icon = s.icon
            return (
              <motion.div
                key={s.title}
                {...stagger(i)}
                className="glass-card rounded-2xl p-6 md:p-8"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${s.bg} flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${s.color}`} />
                  </div>
                  <h2 className="text-lg font-bold text-slate-900 leading-snug pt-1.5">{s.title}</h2>
                </div>
                <div className="pl-14">
                  {s.content}
                </div>
              </motion.div>
            )
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-10 glass-card rounded-2xl p-8 text-center"
        >
          <h3 className="text-xl font-bold text-slate-900 mb-2">¿Tiene alguna pregunta sobre nuestros términos?</h3>
          <p className="text-slate-500 text-sm mb-5 max-w-lg mx-auto">
            Estamos a su disposición para resolver cualquier duda antes de iniciar un proyecto.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="mailto:info@innovatheratech.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition-all shadow-[0_4px_16px_rgba(37,99,235,0.3)] active:scale-95"
            >
              Envíanos un correo
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://calendly.com/innovatheratech/30min"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-slate-200/60 bg-white/60 text-slate-700 font-semibold text-sm hover:bg-white/80 transition-all active:scale-95"
            >
              Agenda una llamada
            </a>
          </div>
        </motion.div>

      </div>
    </section>
  )
}
